import { useState, useMemo } from "react";
import styled, { keyframes } from "styled-components";
import { blueColor, goldColor } from "../../../../styled/Theme";
import {
  PlusOutlined,
  MinusOutlined,
  SearchOutlined,
  MessageOutlined,
  ArrowRightOutlined,
  SafetyCertificateOutlined,
  ApiOutlined,
  UserOutlined,
  WalletOutlined,
  BankOutlined,
} from "@ant-design/icons";

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const faqData: FAQItem[] = [
  // General
  {
    category: "General",
    question: "What is OnePayX?",
    answer:
      "OnePayX is a unified digital payments and communication platform developed by Yoya Technologies. It allows individuals, businesses, SACCOs, and governments to pay bills, URA taxes, IRAS PRNs, municipal fees, and transfer money to agents and merchants — instantly and securely.",
  },
  {
    category: "General",
    question: "Who can use OnePayX?",
    answer:
      "OnePayX is designed for a wide range of users: individuals for personal finance and bill payments, businesses and SMEs for revenue management, SACCOs and financial institutions for member payments, NGOs for disbursements, and local governments for municipal revenue collection. If you pay or receive money, OnePayX is built for you.",
  },
  {
    category: "General",
    question: "How do I get started with OnePayX?",
    answer:
      "Getting started is simple. Download the OnePayX app from the Google Play Store or Apple App Store, register with your phone number and national ID, set a PIN or enable biometrics, and your account is ready. You can also access OnePayX via USSD (*123#) on any mobile phone.",
  },
  // Security
  {
    category: "Security",
    question: "Is OnePayX secure?",
    answer:
      "Yes. OnePayX uses bank-grade security including 256-bit AES encryption, multi-factor authentication (MFA), role-based access controls, real-time fraud detection, and full audit trails. All transactions are processed through regulated partners and escrow-backed settlement mechanisms. We are ISO 27001 certified.",
  },
  {
    category: "Security",
    question: "What happens if I lose my phone?",
    answer:
      "Your funds are safe. OnePayX accounts are tied to your identity, not your device. You can immediately freeze your account via USSD *123# or through our support team. Once you recover or replace your device, simply log back in with your credentials and re-verify your identity.",
  },
  {
    category: "Security",
    question: "Are my transaction records private?",
    answer:
      "Yes. Your transaction data is encrypted at rest and in transit. OnePayX complies with applicable data protection regulations. Records are only shared with verified authorities when required by law, and you maintain full access to your own transaction history at all times.",
  },
  // Integration
  {
    category: "Integration",
    question: "Can OnePayX integrate with existing systems?",
    answer:
      "Yes. OnePayX is built on an API-first architecture that allows seamless integration with existing enterprise, government, ERP, and third-party systems. We provide REST APIs, webhooks, and SDKs for developers. Our technical team supports onboarding and custom integration projects.",
  },
  {
    category: "Integration",
    question: "Do you offer custom solutions?",
    answer:
      "Absolutely. OnePayX provides configurable and tailored solutions to meet the specific operational, regulatory, and reporting needs of organisations and institutions. Contact our enterprise team to discuss your requirements and we'll design a solution that fits your workflows.",
  },
  // Payments
  {
    category: "Payments",
    question: "What types of payments can I make?",
    answer:
      "You can pay utility bills, municipal fees, URA taxes, IRAS PRNs, street parking, SACCO contributions, school fees, and make peer-to-peer transfers. OnePayX also supports salary disbursements, vendor payments, and bulk payment processing for organisations.",
  },
  {
    category: "Payments",
    question: "How long does a payment take to process?",
    answer:
      "Most payments are confirmed in under 30 seconds. Bill payments and government fees are settled in real time with instant receipts sent to both payer and recipient. Bulk payments and institutional transfers may take up to a few minutes depending on volume.",
  },
  {
    category: "Payments",
    question: "What payment methods are supported?",
    answer:
      "OnePayX supports mobile money (MTN, Airtel), bank accounts, debit/credit cards, and the OnePayX in-app wallet. Merchants and institutions can also accept payments via QR codes and USSD. All channels are linked to a single unified account.",
  },
];

const categories = ["All", "General", "Security", "Integration", "Payments"];

const categoryIcons: Record<string, React.ReactNode> = {
  All: <SearchOutlined />,
  General: <UserOutlined />,
  Security: <SafetyCertificateOutlined />,
  Integration: <ApiOutlined />,
  Payments: <WalletOutlined />,
};

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [activeCategory, setActiveCategory] = useState("All");
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    return faqData.filter((item) => {
      const matchCat =
        activeCategory === "All" || item.category === activeCategory;
      const matchSearch =
        search.trim() === "" ||
        item.question.toLowerCase().includes(search.toLowerCase()) ||
        item.answer.toLowerCase().includes(search.toLowerCase());
      return matchCat && matchSearch;
    });
  }, [activeCategory, search]);

  const handleToggle = (idx: number) => {
    setOpenIndex(openIndex === idx ? null : idx);
  };

  return (
    <Wrapper>
      <BgGlow />

      <Inner className="container">
        {/* ── Header ── */}
        <Header data-aos="fade-up" data-aos-duration="700" data-aos-delay="80">
          <Eyebrow>
            <EyebrowDot />
            FAQ
          </Eyebrow>
          <Title>
            Frequently Asked <GoldWord>Questions</GoldWord>
          </Title>
          <Subtitle>
            Everything you need to know about OnePayX. Can't find what you're
            looking for?{" "}
            <ContactLink href="/contact">
              Talk to our team <ArrowRightOutlined />
            </ContactLink>
          </Subtitle>
        </Header>

        {/* ── Search ── */}
        <SearchRow
          data-aos="fade-up"
          data-aos-duration="600"
          data-aos-delay="160"
        >
          <SearchWrap>
            <SearchIcon>
              <SearchOutlined />
            </SearchIcon>
            <SearchInput
              placeholder="Search questions…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setOpenIndex(null);
              }}
            />
            {search && (
              <SearchClear onClick={() => setSearch("")}>✕</SearchClear>
            )}
          </SearchWrap>
        </SearchRow>

        {/* ── Category tabs ── */}
        <CategoryRow
          data-aos="fade-up"
          data-aos-duration="600"
          data-aos-delay="220"
        >
          {categories.map((cat) => (
            <CategoryTab
              key={cat}
              $active={activeCategory === cat}
              onClick={() => {
                setActiveCategory(cat);
                setOpenIndex(null);
              }}
            >
              <TabIcon>{categoryIcons[cat]}</TabIcon>
              {cat}
            </CategoryTab>
          ))}
        </CategoryRow>

        {/* ── Main content ── */}
        <ContentGrid>
          {/* Left: accordion */}
          <AccordionCol>
            {filtered.length === 0 ? (
              <EmptyState>
                <EmptyIcon>🔍</EmptyIcon>
                <EmptyText>No questions match your search.</EmptyText>
                <EmptyAction
                  onClick={() => {
                    setSearch("");
                    setActiveCategory("All");
                  }}
                >
                  Clear filters
                </EmptyAction>
              </EmptyState>
            ) : (
              filtered.map((item, i) => {
                const isOpen = openIndex === i;
                return (
                  <AosWrapper
                    key={`${item.category}-${item.question}`}
                    data-aos="fade-up"
                    data-aos-delay={i * 60}
                    data-aos-duration="500"
                    data-aos-once="true"
                  >
                    <AccordionItem $open={isOpen}>
                      <AccordionTrigger
                        onClick={() => handleToggle(i)}
                        $open={isOpen}
                      >
                        <TriggerLeft>
                          <CategoryDot $cat={item.category} />
                          <QuestionText $open={isOpen}>
                            {item.question}
                          </QuestionText>
                        </TriggerLeft>
                        <ToggleBtn $open={isOpen}>
                          {isOpen ? <MinusOutlined /> : <PlusOutlined />}
                        </ToggleBtn>
                      </AccordionTrigger>

                      <AccordionBody $open={isOpen}>
                        <AnswerInner>
                          <AnswerText>{item.answer}</AnswerText>
                          <AnswerTag $cat={item.category}>
                            {categoryIcons[item.category]}
                            {item.category}
                          </AnswerTag>
                        </AnswerInner>
                      </AccordionBody>
                    </AccordionItem>
                  </AosWrapper>
                );
              })
            )}
          </AccordionCol>

          {/* Right: contact card + stats */}
          <SideCol>
            <ContactCard
              data-aos="fade-left"
              data-aos-duration="700"
              data-aos-delay="300"
            >
              <ContactCardIcon>
                <MessageOutlined />
              </ContactCardIcon>
              <ContactCardTitle>Still have questions?</ContactCardTitle>
              <ContactCardSub>
                Our support team is available around the clock to help with
                anything you need.
              </ContactCardSub>
              <ContactCardBtn href="/contact">
                Contact Support <ArrowRightOutlined />
              </ContactCardBtn>
              <ContactCardBtn $outline href="tel:+256800100200">
                <BankOutlined /> Call us: 0800 100 200
              </ContactCardBtn>
              <SupportHours>
                <HoursDot />
                Support available 24 / 7
              </SupportHours>
            </ContactCard>

            <StatsCard
              data-aos="fade-left"
              data-aos-duration="700"
              data-aos-delay="420"
            >
              <StatsRow>
                <StatBox>
                  <StatVal>12k+</StatVal>
                  <StatLabel>Happy users</StatLabel>
                </StatBox>
                <StatDiv />
                <StatBox>
                  <StatVal>99.9%</StatVal>
                  <StatLabel>Uptime SLA</StatLabel>
                </StatBox>
              </StatsRow>
            </StatsCard>
          </SideCol>
        </ContentGrid>
      </Inner>
    </Wrapper>
  );
}

/* ── Animations ── */
const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const catColors: Record<string, string> = {
  General: blueColor,
  Security: "#22c55e",
  Integration: "#a855f7",
  Payments: goldColor,
};

/* ── Styles ── */

const Wrapper = styled.section`
  background: #f8fafc;
  padding: 80px 0 88px;
  position: relative;
  overflow: hidden;
`;

const BgGlow = styled.div`
  position: absolute;
  top: -100px;
  right: -100px;
  width: 400px;
  height: 400px;
  border-radius: 50%;
  background: radial-gradient(circle, ${blueColor}0c 0%, transparent 70%);
  pointer-events: none;
`;

const Inner = styled.div`
  padding: 0 20px;
`;

/* Header */
const Header = styled.div`
  text-align: center;
  max-width: 640px;
  margin: 0 auto 40px;
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.1px;
  text-transform: uppercase;
  color: ${blueColor};
  background: ${blueColor}0f;
  border: 1px solid ${blueColor}28;
  border-radius: 20px;
  padding: 4px 14px;
  margin-bottom: 16px;
`;

const EyebrowDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${goldColor};
`;

const Title = styled.h2`
  font-size: 38px;
  font-weight: 900;
  color: #0a1628;
  letter-spacing: -1.2px;
  line-height: 1.15;
  margin: 0 0 14px;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const GoldWord = styled.span`
  color: ${goldColor};
`;

const Subtitle = styled.p`
  font-size: 15px;
  color: #64748b;
  line-height: 1.65;
  margin: 0;
`;

const ContactLink = styled.a`
  color: ${blueColor};
  font-weight: 600;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &:hover {
    text-decoration: underline;
  }
`;

/* Search */
const SearchRow = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
`;

const SearchWrap = styled.div`
  position: relative;
  width: 100%;
  max-width: 520px;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 16px;
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  background: #ffffff;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 13px 44px 13px 44px;
  font-size: 14px;
  color: #0f172a;
  outline: none;
  transition:
    border-color 0.2s,
    box-shadow 0.2s;
  box-sizing: border-box;

  &::placeholder {
    color: #94a3b8;
  }

  &:focus {
    border-color: ${blueColor}60;
    box-shadow: 0 0 0 3px ${blueColor}12;
  }
`;

const SearchClear = styled.button`
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 13px;
  cursor: pointer;
  padding: 2px 4px;

  &:hover {
    color: #334155;
  }
`;

/* Category tabs */
const CategoryRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 40px;
`;

const CategoryTab = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  font-weight: 600;
  border-radius: 10px;
  padding: 7px 16px;
  border: 1.5px solid ${(p) => (p.$active ? blueColor : "#e2e8f0")};
  background: ${(p) => (p.$active ? blueColor : "#ffffff")};
  color: ${(p) => (p.$active ? "#fff" : "#475569")};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${blueColor};
    color: ${(p) => (p.$active ? "#fff" : blueColor)};
  }
`;

const TabIcon = styled.span`
  font-size: 13px;
`;

/* Main grid */
const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 28px;
  align-items: flex-start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

/* Accordion */
const AosWrapper = styled.div``;

const AccordionCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const AccordionItem = styled.div<{ $open: boolean }>`
  background: #ffffff;
  border: 1.5px solid ${(p) => (p.$open ? blueColor + "50" : "#e8f0f8")};
  border-radius: 16px;
  overflow: hidden;
  transition:
    border-color 0.25s,
    box-shadow 0.25s;
  box-shadow: ${(p) =>
    p.$open ? `0 8px 28px ${blueColor}10` : "0 2px 8px rgba(0,0,0,0.04)"};
`;

const AccordionTrigger = styled.button<{ $open: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 22px;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  transition: background 0.2s;

  &:hover {
    background: ${blueColor}04;
  }
`;

const TriggerLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const CategoryDot = styled.div<{ $cat: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => catColors[p.$cat] || blueColor};
  flex-shrink: 0;
`;

const QuestionText = styled.div<{ $open: boolean }>`
  font-size: 15px;
  font-weight: ${(p) => (p.$open ? "700" : "600")};
  color: ${(p) => (p.$open ? "#0a1628" : "#1e293b")};
  line-height: 1.4;
  transition: color 0.2s;
`;

const ToggleBtn = styled.div<{ $open: boolean }>`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: ${(p) => (p.$open ? blueColor : "#f1f5f9")};
  color: ${(p) => (p.$open ? "#fff" : "#64748b")};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  flex-shrink: 0;
  transition: all 0.22s;
`;

const AccordionBody = styled.div<{ $open: boolean }>`
  max-height: ${(p) => (p.$open ? "500px" : "0")};
  overflow: hidden;
  transition: max-height 0.35s cubic-bezier(0.4, 0, 0.2, 1);
`;

const AnswerInner = styled.div`
  padding: 0 22px 20px 42px;
  animation: ${slideDown} 0.3s ease;
`;

const AnswerText = styled.p`
  font-size: 14px;
  color: #475569;
  line-height: 1.75;
  margin: 0 0 14px;
`;

const AnswerTag = styled.div<{ $cat: string }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${(p) => catColors[p.$cat] || blueColor};
  background: ${(p) => (catColors[p.$cat] || blueColor) + "12"};
  border-radius: 6px;
  padding: 3px 9px;
`;

/* Empty state */
const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
`;

const EmptyIcon = styled.div`
  font-size: 40px;
  margin-bottom: 12px;
`;

const EmptyText = styled.div`
  font-size: 15px;
  color: #64748b;
  margin-bottom: 14px;
`;

const EmptyAction = styled.button`
  background: none;
  border: 1.5px solid ${blueColor};
  color: ${blueColor};
  font-size: 13px;
  font-weight: 600;
  border-radius: 8px;
  padding: 7px 18px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${blueColor};
    color: #fff;
  }
`;

/* Side col */
const SideCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: sticky;
  top: 100px;

  @media (max-width: 900px) {
    position: static;
  }
`;

const ContactCard = styled.div`
  background: linear-gradient(145deg, #07142e 0%, #0d2254 100%);
  border-radius: 20px;
  padding: 28px 24px;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: -60px;
    right: -60px;
    width: 200px;
    height: 200px;
    border-radius: 50%;
    background: radial-gradient(circle, ${blueColor}30 0%, transparent 70%);
    pointer-events: none;
  }
`;

const ContactCardIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: ${blueColor};
  color: #fff;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
  position: relative;
  z-index: 1;
`;

const ContactCardTitle = styled.div`
  font-size: 16px;
  font-weight: 800;
  color: #ffffff;
  margin-bottom: 8px;
  position: relative;
  z-index: 1;
`;

const ContactCardSub = styled.p`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.6;
  margin: 0 0 18px;
  position: relative;
  z-index: 1;
`;

const ContactCardBtn = styled.a<{ $outline?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  border-radius: 10px;
  padding: 11px 16px;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.22s;
  margin-bottom: 10px;
  position: relative;
  z-index: 1;

  background: ${(p) => (p.$outline ? "transparent" : "#fff")};
  color: ${(p) => (p.$outline ? "rgba(255,255,255,0.7)" : blueColor)};
  border: 1.5px solid
    ${(p) => (p.$outline ? "rgba(255,255,255,0.2)" : "transparent")};

  &:hover {
    background: ${(p) => (p.$outline ? "rgba(255,255,255,0.08)" : "#f1f5f9")};
    color: ${(p) => (p.$outline ? "#fff" : blueColor)};
  }
`;

const SupportHours = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.38);
  font-weight: 500;
  justify-content: center;
  position: relative;
  z-index: 1;
`;

const HoursDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #22c55e;
`;

/* Stats card */
const StatsCard = styled.div`
  background: #fff;
  border: 1px solid #e8f0f8;
  border-radius: 16px;
  padding: 18px 20px;
`;

const StatsRow = styled.div`
  display: flex;
  align-items: center;
`;

const StatBox = styled.div`
  flex: 1;
  text-align: center;
`;

const StatVal = styled.div`
  font-size: 24px;
  font-weight: 900;
  color: ${blueColor};
  letter-spacing: -0.8px;
`;

const StatLabel = styled.div`
  font-size: 11px;
  color: #94a3b8;
  font-weight: 500;
  margin-top: 2px;
`;

const StatDiv = styled.div`
  width: 1px;
  height: 36px;
  background: #e2e8f0;
`;
